import React, { useState, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  Chip,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  withStyles,
  IconButton,
  Tooltip,
  TextField,
  Checkbox,
  Button,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { showGlobalToast } from 'src/features/uiSlice';
import { messageToastType_const } from 'src/app/constant/config';
import { isAgencyCustomerOrder } from './orderUtils';

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#f5f6fa',
    color: '#45485E',
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: 'nowrap',
    borderBottom: '2px solid #e0e3ef'
  },
  body: {
    fontSize: 13,
    whiteSpace: 'nowrap',
    color: '#333'
  },
  root: { padding: '10px 16px' }
}))(TableCell);

const getRoleDisplayName = (userOrRole, fallback = '') => {
  if (!userOrRole) return fallback || '';

  if (typeof userOrRole === 'string') {
    const raw = userOrRole.trim().toLowerCase();
    if (raw.includes('admin') || raw.includes('quản trị')) return 'Quản trị';
    if (raw.includes('kế toán') || raw.includes('accountant')) return 'Kế toán';
    if (raw.includes('cskh') || raw.includes('customer_care') || raw.includes('chăm sóc')) return 'CSKH';
    if (raw.includes('kỹ thuật') || raw.includes('technician') || raw.includes('tech')) return 'Kỹ thuật';
    if (raw.includes('lead') || raw.includes('marketing')) return 'Lead Marketing';
    if (raw.includes('đại lý') || raw.includes('agency')) return 'Đại lý';
    if (raw.includes('sale') || raw.includes('bán hàng')) return 'Sale';
    if (raw.includes('tài xế') || raw.includes('driver')) return 'Tài xế';
    if (raw.includes('enduser') || raw.includes('customer')) return 'Sale';
    if (raw.includes('hệ thống')) return 'Hệ thống';
    if (userOrRole.trim()) return userOrRole.trim();
  }

  const roleId = Number(
    userOrRole?.role_id ||
    userOrRole?.roleId ||
    userOrRole?.role?.id ||
    (typeof userOrRole === 'number' ? userOrRole : 0)
  );

  const rawRole = String(
    userOrRole?.role?.title ||
    userOrRole?.role ||
    userOrRole?.role_name ||
    userOrRole?.roleName ||
    ''
  ).toLowerCase().trim();

  if (roleId === 1 || rawRole === 'admin' || rawRole.includes('quản trị')) return 'Quản trị';
  if (roleId === 2 || rawRole === 'sale' || rawRole.includes('bán hàng')) return 'Sale';
  if (roleId === 3 || rawRole === 'customer_care' || rawRole.includes('chăm sóc') || rawRole.includes('cskh')) return 'CSKH';
  if (roleId === 4 || rawRole === 'agency' || rawRole.includes('đại lý')) return 'Đại lý';
  if (roleId === 7 || rawRole === 'accountant' || rawRole.includes('kế toán')) return 'Kế toán';
  if (roleId === 8 || rawRole === 'technician' || rawRole.includes('kỹ thuật') || rawRole.includes('tech')) return 'Kỹ thuật';
  if (roleId === 10 || rawRole === 'driver' || rawRole.includes('tài xế')) return 'Tài xế';
  if (roleId === 11 || rawRole === 'lead_marketing' || rawRole.includes('lead') || rawRole.includes('marketing')) return 'Lead Marketing';
  if (roleId === 5 || rawRole.includes('enduser') || rawRole.includes('customer')) return 'Sale';

  if (rawRole.includes('admin') || rawRole.includes('quản trị')) return 'Quản trị';
  if (rawRole.includes('accountant') || rawRole.includes('kế toán')) return 'Kế toán';
  if (rawRole.includes('cskh') || rawRole.includes('chăm sóc')) return 'CSKH';
  if (rawRole.includes('kỹ thuật') || rawRole.includes('tech')) return 'Kỹ thuật';
  if (rawRole.includes('sale')) return 'Sale';
  if (rawRole.includes('marketing')) return 'Lead Marketing';

  if (userOrRole?.role?.title) return userOrRole.role.title;
  if (userOrRole?.role_name && !userOrRole.role_name.toLowerCase().includes('enduser')) return userOrRole.role_name;

  return fallback || (roleId ? `Role ${roleId}` : 'Sale');
};

const OrdersTable = ({
  paginated,
  page,
  PAGE_SIZE,
  classes,
  isAdmin,
  isAccountant,
  isSale,
  isLeadMarketing,
  isAgency,
  canManageReceipts,
  receiptsByOrderId = {},
  listUsers = [],
  listStaff = [],
  listSales = [],
  dataLogin = null,
  onOpenAddReceipt,
  onEditReceipt,
  onDeleteReceipt,
  getOrderPaymentInfo,
  agencyDebtMap = {},
  formatMoney,
  formatInputMoney,
  getPaymentStyle,
  STATUS_CONFIG,
  isScrolled,
  isDown,
  onMouseDown,
  onMouseLeave,
  onMouseUp,
  onMouseMove,
  handleScroll,
  scrollContainerRef,
  localExtraFees,
  setLocalExtraFees,
  handleSaveExtraFee,
  localTrackingUrls,
  setLocalTrackingUrls,
  handleSaveTrackingUrl,
  handleDeliveredToggle,
  setEditingOrder,
  setEditingDevices,
  setOrderForImage,
  setOpenImageModal,
  setSelectedGallery,
  handleMenuOpen,
  handleCompleteOrder,
  listDeviceType,
  total,
  totalAmountSum,
  totalPaidSum,
  amountBreakdown,
  setPage,
  onViewDetailOrder,
  onOpenSerialModal,
  listAgencies = [],
  unifiedSummary = null,
  isUnifiedMode = false,
  transactionTypeFilter = 'ALL',
  receiptTotalAmountSum = 0
}) => {
  const dispatch = useDispatch();
  const startCoordsRef = useRef({ x: 0, y: 0 });
  const dragDistanceRef = useRef(0);

  const reduxAgencies = useSelector(state => state.agencySlice?.listAgencies || []);
  const allAgencies = (listAgencies && listAgencies.length > 0) ? listAgencies : reduxAgencies;

  const flatAgencies = useMemo(() => {
    const flatten = (arr) => {
      const res = [];
      (arr || []).forEach(a => {
        if (!a) return;
        res.push(a);
        if (a.children && a.children.length > 0) {
          res.push(...flatten(a.children));
        }
      });
      return res;
    };
    return flatten(allAgencies);
  }, [allAgencies]);

  const agencyPhoneMap = useMemo(() => {
    const map = {};
    flatAgencies.forEach(ag => {
      const phone = ag.leader?.phone || ag.phone || ag.phone_number || '';
      if (phone) {
        if (ag.id) map[String(ag.id)] = phone;
        const normName = (ag.name || ag.fullname || '').trim().toLowerCase();
        if (normName) map[normName] = phone;
        const normUser = (ag.username || ag.leader?.username || '').trim().toLowerCase();
        if (normUser) map[normUser] = phone;
      }
    });

    (paginated || []).forEach(item => {
      if (!item || item.type === 'receipt') return;
      const order = item;
      const oPhone =
        order.end_user?.phone ||
        order.agency?.leader?.phone ||
        order.agency?.phone ||
        order.customer_phone ||
        order.phone ||
        '';
      if (oPhone) {
        if (order.agency_id) map[String(order.agency_id)] = oPhone;
        if (order.agency?.id) map[String(order.agency.id)] = oPhone;
        const agName = (order.agency?.name || '').trim().toLowerCase();
        if (agName) map[agName] = oPhone;
        const cName = (order.end_user?.full_name || order.customer_name || '').trim().toLowerCase();
        if (cName) map[cName] = oPhone;
      }
    });

    return map;
  }, [flatAgencies, paginated]);

  const agencyAddressMap = useMemo(() => {
    const map = {};
    flatAgencies.forEach(ag => {
      const addr = ag.leader?.address || ag.address || ag.company || '';
      if (addr) {
        if (ag.id) map[String(ag.id)] = addr;
        const normName = (ag.name || ag.fullname || '').trim().toLowerCase();
        if (normName) map[normName] = addr;
        const normUser = (ag.username || ag.leader?.username || '').trim().toLowerCase();
        if (normUser) map[normUser] = addr;
      }
    });

    (paginated || []).forEach(item => {
      if (!item) return;
      const oAddr =
        item.address ||
        item.customer_address ||
        item.shipping_address ||
        item.end_user?.address ||
        item.agency?.leader?.address ||
        item.agency?.address ||
        item.company ||
        '';
      if (oAddr) {
        if (item.agency_id) map[String(item.agency_id)] = oAddr;
        if (item.agency?.id) map[String(item.agency.id)] = oAddr;
        if (item.payer_id) map[String(item.payer_id)] = oAddr;
        const agName = (item.agency?.name || '').trim().toLowerCase();
        if (agName) map[agName] = oAddr;
        const cName = (item.end_user?.full_name || item.customer_name || item.payer_name || (typeof item.counterparty === 'string' ? item.counterparty : item.counterparty?.name) || '').trim().toLowerCase();
        if (cName) map[cName] = oAddr;
      }
    });

    return map;
  }, [flatAgencies, paginated]);

  const usersLookupMap = useMemo(() => {
    const byId = {};
    const byName = {};
    const byUsername = {};

    const register = (u, defaultRole = '') => {
      if (!u) return;
      const uid = u.id ? String(u.id) : null;
      const fn = (u.full_name || u.name || '').trim().toLowerCase();
      const un = (u.username || '').trim().toLowerCase();

      const userRole = u.role?.title || (typeof u.role === 'string' ? u.role : '') || u.role_name || defaultRole;
      const roleId = Number(u.role_id || u.roleId || u.role?.id || (defaultRole === 'Sale' ? 2 : 0));

      const entry = {
        ...u,
        role_id: roleId,
        role_name: userRole,
        role: u.role || (userRole ? { id: roleId, title: userRole } : undefined)
      };

      if (uid) byId[uid] = entry;
      if (fn) byName[fn] = entry;
      if (un) byUsername[un] = entry;
    };

    // ONLY staff and sales, never customers
    (listSales || []).forEach(u => register(u, 'Sale'));
    (listStaff || []).forEach(u => register(u, ''));
    if (dataLogin) register(dataLogin, '');

    return { byId, byName, byUsername };
  }, [listSales, listStaff, dataLogin]);




  const [receiptMenuAnchor, setReceiptMenuAnchor] = useState(null);
  const [menuReceipt, setMenuReceipt] = useState(null);

  const handleReceiptMenuOpen = (e, receipt) => {
    e.stopPropagation();
    setReceiptMenuAnchor(e.currentTarget);
    setMenuReceipt(receipt);
  };

  const handleReceiptMenuClose = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setReceiptMenuAnchor(null);
    setMenuReceipt(null);
  };

  const handleContainerMouseDown = e => {
    startCoordsRef.current = { x: e.clientX, y: e.clientY };
    dragDistanceRef.current = 0;
    if (onMouseDown) onMouseDown(e);
  };

  const handleContainerMouseMove = e => {
    if (startCoordsRef.current) {
      const dx = Math.abs(e.clientX - startCoordsRef.current.x);
      const dy = Math.abs(e.clientY - startCoordsRef.current.y);
      dragDistanceRef.current = Math.max(dragDistanceRef.current, dx + dy);
    }
    if (onMouseMove) onMouseMove(e);
  };

  const handleRowClick = (e, order) => {
    if (dragDistanceRef.current > 5) {
      return;
    }
    if (onViewDetailOrder) {
      onViewDetailOrder(order);
    }
  };

  const handleCopyPhone = (e, phone) => {
    e.stopPropagation();
    if (!phone || phone === '--') return;
    const textToCopy = String(phone).trim();

    const fallbackCopy = text => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      dispatch(
        showGlobalToast({
          message: `Đã sao chép SĐT: ${text}`,
          type: messageToastType_const.success
        })
      );
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        dispatch(
          showGlobalToast({
            message: `Đã sao chép SĐT: ${textToCopy}`,
            type: messageToastType_const.success
          })
        );
      }).catch(() => {
        fallbackCopy(textToCopy);
      });
    } else {
      fallbackCopy(textToCopy);
    }
  };

  return (
    <Card className={classes.tableCard}>
      <Box
        ref={scrollContainerRef}
        onMouseDown={handleContainerMouseDown}
        onMouseLeave={onMouseLeave}
        onMouseUp={onMouseUp}
        onMouseMove={handleContainerMouseMove}
        onScroll={handleScroll}
        style={{
          cursor: isDown ? 'grabbing' : 'grab',
          overflowX: 'auto',
          overflowY: 'hidden',
          userSelect: isDown ? 'none' : 'auto',
          width: '100%'
        }}
      >
        <Table
          style={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            minWidth: 1500
          }}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell
                className={classes.stickyHeader}
                style={{ left: 0, width: 80, minWidth: 80 }}
              >
                Thời gian
              </StyledTableCell>
              <StyledTableCell
                className={classes.stickyHeader}
                style={{ left: 80, width: 110, minWidth: 110 }}
              >
                Mã đơn
              </StyledTableCell>
              <StyledTableCell
                className={classes.stickyHeader}
                style={{ left: 190, width: 120, minWidth: 120 }}
              >
                Nhân viên
              </StyledTableCell>
              <StyledTableCell
                className={classes.stickyHeader}
                style={{
                  left: 310,
                  width: 190,
                  minWidth: 190,
                  borderRight: isScrolled
                    ? '1px solid rgba(0,0,0,0.8)'
                    : 'none',
                  boxShadow: isScrolled
                    ? '6px 0 10px rgba(0,0,0,0.15)'
                    : 'none',
                  transition: 'border-right 0.2s, box-shadow 0.2s'
                }}
              >
                <span style={{ color: '#3f51b5' }}>Đại lý</span>/Khách lẻ
              </StyledTableCell>
              <StyledTableCell style={{ minWidth: 220 }}>Địa chỉ</StyledTableCell>
              <StyledTableCell style={{ minWidth: 320 }}>Ghi chú</StyledTableCell>
              <StyledTableCell>Chi tiết thiết bị</StyledTableCell>
              <StyledTableCell>Serial</StyledTableCell>
              <StyledTableCell>Sim</StyledTableCell>
              <StyledTableCell>PT kích hoạt</StyledTableCell>
              <StyledTableCell>Phân loại đơn</StyledTableCell>
              <StyledTableCell>HT lắp đặt</StyledTableCell>
              <StyledTableCell align="center">Tải ảnh</StyledTableCell>
              <StyledTableCell>Ảnh lắp đặt</StyledTableCell>
              <StyledTableCell>Tiền thiết bị</StyledTableCell>
              <StyledTableCell>Phí ship & Lắp đặt</StyledTableCell>
              <StyledTableCell>Tổng tiền</StyledTableCell>
              <StyledTableCell>Hình thức TT</StyledTableCell>
              <StyledTableCell style={{ minWidth: 175 }}>
                Thanh toán / Thu tiền
              </StyledTableCell>
              <StyledTableCell>Link vận đơn</StyledTableCell>
              <StyledTableCell align="center">Đã giao hàng</StyledTableCell>
              <StyledTableCell>Trạng thái</StyledTableCell>
              <StyledTableCell align="center">Thao tác</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length > 0 ? (
              paginated.map((item, idx) => {
                if (item.rowType === 'RECEIPT' || item.type === 'receipt') {
                  const receipt = item;
                  const rCreatorId = receipt.creator?.id || receipt.created_by;
                  const rawStaffName = (
                    receipt.created_by_name ||
                    receipt.creator?.name ||
                    receipt.creator?.full_name ||
                    receipt.creator_name ||
                    receipt.created_by?.full_name ||
                    receipt.created_by?.name ||
                    receipt.sale?.full_name ||
                    receipt.sale?.name ||
                    ''
                  ).trim();
                  const rStaffName = (rawStaffName || 'Hệ thống').split('(')[0].trim();

                  let rStaffRole = 'Hệ thống';
                  if (rStaffName && rStaffName !== 'Hệ thống') {
                    if (rawStaffName.includes('(')) {
                      rStaffRole = rawStaffName.split('(')[1].replace(')', '').trim();
                    } else {
                      const matchedUser =
                        (rCreatorId && usersLookupMap.byId[String(rCreatorId)]) ||
                        (rStaffName && usersLookupMap.byName[rStaffName.toLowerCase()]) ||
                        (rStaffName && usersLookupMap.byUsername[rStaffName.toLowerCase()]) ||
                        (dataLogin && (dataLogin.id === rCreatorId || (dataLogin.full_name && dataLogin.full_name.trim().toLowerCase() === rStaffName.toLowerCase())) ? dataLogin : null) ||
                        receipt.creator;

                      rStaffRole = getRoleDisplayName(
                        matchedUser,
                        receipt.created_by_role ||
                          receipt.creator?.role_name ||
                          receipt.creator?.role?.title ||
                          'Sale'
                      );
                    }
                  }

                  return (
                    <TableRow
                      key={`receipt-${receipt.id || idx}`}
                      hover
                      className={`${classes.tableRow} ${classes.receiptRow || ''}`}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: '#e6f7f5'
                      }}
                      onClick={e => {
                        if (dragDistanceRef.current > 5) return;
                        if (onEditReceipt) {
                          onEditReceipt(receipt);
                        }
                      }}
                    >
                      <StyledTableCell
                        className={classes.stickyCol}
                        style={{
                          left: 0,
                          width: 80,
                          minWidth: 80,
                          cursor: 'pointer',
                          backgroundColor: '#e6f7f5'
                        }}
                      >
                        <Box display="flex" flexDirection="column">
                          <Typography
                            variant="body2"
                            style={{ fontSize: 13, color: '#1a1f36', fontWeight: 600 }}
                          >
                            {moment(receipt.receipt_date || receipt.created_at || receipt.createdAt || receipt.date).format('DD/MM/YYYY')}
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{ fontSize: 12, color: '#888' }}
                          >
                            {(() => {
                              const timeSource =
                                (receipt.created_at && (String(receipt.created_at).includes('T') || String(receipt.created_at).includes(':')) ? receipt.created_at : null) ||
                                (receipt.createdAt && (String(receipt.createdAt).includes('T') || String(receipt.createdAt).includes(':')) ? receipt.createdAt : null) ||
                                (receipt.receipt_date && (String(receipt.receipt_date).includes('T') || String(receipt.receipt_date).includes(':')) ? receipt.receipt_date : null);
                              return timeSource ? moment(timeSource).format('HH:mm') : '--';
                            })()}
                          </Typography>
                        </Box>
                      </StyledTableCell>

                      <StyledTableCell
                        className={classes.stickyCol}
                        style={{
                          left: 80,
                          width: 110,
                          minWidth: 110,
                          cursor: 'pointer',
                          backgroundColor: '#e6f7f5'
                        }}
                      >
                        <Typography style={{ fontSize: 13, fontWeight: 700, color: '#1a1f36' }}>
                          {receipt.code || `#PT-${receipt.id}`}
                        </Typography>
                      </StyledTableCell>

                      <StyledTableCell
                        className={classes.stickyCol}
                        style={{ left: 190, width: 120, minWidth: 120, cursor: 'pointer', backgroundColor: '#e6f7f5' }}
                      >
                        <Box display="flex" flexDirection="column">
                          <Typography
                            variant="body2"
                            style={{ fontSize: 13, fontWeight: 600, color: '#1a1f36' }}
                          >
                            {rStaffName || 'Hệ thống'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>
                            {rStaffRole}
                          </Typography>
                        </Box>
                      </StyledTableCell>

                      <StyledTableCell
                        className={classes.stickyCol}
                        style={{
                          left: 310,
                          width: 190,
                          minWidth: 190,
                          cursor: 'pointer',
                          backgroundColor: '#e6f7f5',
                          borderRight: isScrolled ? '1px solid rgba(0,0,0,0.8)' : 'none',
                          boxShadow: isScrolled ? '6px 0 10px rgba(0,0,0,0.15)' : 'none'
                        }}
                      >
                        <Box display="flex" flexDirection="column">
                          <Tooltip title={receipt.payer_name || receipt.customer_name || '--'} arrow placement="top-start">
                            <Typography
                              className={classes.customerName}
                              style={{
                                color: receipt.payer_type === 'AGENCY' ? '#3f51b5' : '#1a1f36',
                                whiteSpace: 'normal'
                              }}
                            >
                              {receipt.payer_name || receipt.customer_name || '--'}
                            </Typography>
                          </Tooltip>
                          {(() => {
                            const agId = String(receipt.payer_id || receipt.agency_id || receipt.agency?.id || '');
                            const pNameKey = (
                              receipt.payer_name ||
                              receipt.customer_name ||
                              (typeof receipt.counterparty === 'string' ? receipt.counterparty : receipt.counterparty?.name) ||
                              ''
                            ).trim().toLowerCase();
                            const agFromMap = agId && agencyDebtMap ? agencyDebtMap[agId] : null;
                            const phoneNum =
                              receipt.phone ||
                              receipt.customer_phone ||
                              receipt.payer_phone ||
                              receipt.agency?.phone ||
                              receipt.agency?.leader?.phone ||
                              agFromMap?.phone ||
                              (agId && agencyPhoneMap[agId]) ||
                              (pNameKey && agencyPhoneMap[pNameKey]) ||
                              '';
                            return phoneNum ? (
                              <Tooltip title="Click để sao chép SĐT" arrow placement="bottom-start">
                                <Box
                                  display="inline-flex"
                                  alignItems="center"
                                  style={{ cursor: 'pointer', gap: 4, width: 'fit-content', marginTop: 2 }}
                                  onClick={e => handleCopyPhone(e, phoneNum)}
                                >
                                  <Typography
                                    variant="body2"
                                    style={{
                                      fontSize: 13,
                                      color: '#666',
                                      fontWeight: 500,
                                      transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={e => (e.target.style.color = '#3f51b5')}
                                    onMouseLeave={e => (e.target.style.color = '#666')}
                                  >
                                    {phoneNum}
                                  </Typography>
                                  <FileCopyOutlinedIcon
                                    style={{
                                      fontSize: 13,
                                      color: '#888',
                                      transition: 'color 0.2s'
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography
                                variant="body2"
                                style={{ fontSize: 13, color: '#888' }}
                              >
                                --
                              </Typography>
                            );
                          })()}
                        </Box>
                      </StyledTableCell>

                      <StyledTableCell style={{ minWidth: 220, cursor: 'pointer' }}>
                        <Typography variant="body2" style={{ fontSize: 12, color: '#555' }}>
                          {(() => {
                            const agId = String(receipt.payer_id || receipt.agency_id || receipt.agency?.id || '');
                            const pNameKey = (
                              receipt.payer_name ||
                              receipt.customer_name ||
                              (typeof receipt.counterparty === 'string' ? receipt.counterparty : receipt.counterparty?.name) ||
                              ''
                            ).trim().toLowerCase();
                            return (
                              receipt.address ||
                              receipt.customer_address ||
                              receipt.company ||
                              receipt.agency?.leader?.address ||
                              receipt.agency?.address ||
                              (agId && agencyAddressMap[agId]) ||
                              (pNameKey && agencyAddressMap[pNameKey]) ||
                              '--'
                            );
                          })()}
                        </Typography>
                      </StyledTableCell>

                      <StyledTableCell style={{ minWidth: 320, cursor: 'pointer' }}>
                        <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
                          {receipt.order_code && (
                            <Chip
                              size="small"
                              label={`Đơn: ${receipt.order_code.startsWith('#') ? receipt.order_code : `#${receipt.order_code}`}`}
                              style={{
                                height: 20,
                                fontSize: 11,
                                fontWeight: 700,
                                background: '#e0f2fe',
                                color: '#0369a1',
                                border: '1px solid #bae6fd',
                                width: 'fit-content'
                              }}
                            />
                          )}
                          <Typography variant="body2" style={{ fontSize: 12, color: '#444' }}>
                            {receipt.note || '--'}
                          </Typography>
                        </Box>
                      </StyledTableCell>

                      {/* Col 6: Chi tiết thiết bị (Phiếu thu không có thiết bị) */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>--</StyledTableCell>

                      {/* Col 7: Serial */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>--</StyledTableCell>

                      {/* Col 8: Sim */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>--</StyledTableCell>

                      {/* Col 9: PT kích hoạt */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>--</StyledTableCell>

                      {/* Col 10: Phân loại đơn */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>
                        <Typography variant="body2" style={{ fontSize: 13, color: '#333' }}>
                          Thu tiền
                        </Typography>
                      </StyledTableCell>

                      {/* Col 11: HT lắp đặt */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>--</StyledTableCell>

                      {/* Col 12: Tải ảnh */}
                      <StyledTableCell align="center" style={{ cursor: 'pointer' }}>--</StyledTableCell>

                      {/* Col 13: Ảnh lắp đặt */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>--</StyledTableCell>

                      {/* Col 14: Tiền thiết bị */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>--</StyledTableCell>

                      {/* Col 15: Phí ship & Lắp đặt */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>--</StyledTableCell>

                      {/* Col 16: Tổng tiền */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>
                        <Box display="flex" flexDirection="column" alignItems="flex-start">
                          <Typography style={{ fontSize: 14, fontWeight: 700, color: '#2e7d32' }}>
                            + {formatMoney(receipt.amount || 0)}
                          </Typography>
                        </Box>
                      </StyledTableCell>

                      {/* Col 17: Hình thức TT */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>
                        {(() => {
                          const method = receipt.account || receipt.payment_method_name || 'Tiền mặt';
                          const style = getPaymentStyle ? getPaymentStyle(method) : { color: '#27ae60', bg: '#eafaf1' };
                          return (
                            <Typography
                              variant="body2"
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: style.color,
                                background: style.bg,
                                padding: '4px 10px',
                                borderRadius: 12,
                                display: 'inline-block',
                                border: `1px solid ${style.color}22`
                              }}
                            >
                              {method}
                            </Typography>
                          );
                        })()}
                      </StyledTableCell>

                      {/* Col 18: Thanh toán / Thu tiền */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>
                        <Chip
                          label="Đã thu tiền"
                          size="small"
                          style={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 700,
                            background: '#eafaf1',
                            color: '#27ae60',
                            border: '1px solid #27ae6044'
                          }}
                        />
                      </StyledTableCell>

                      {/* Col 19: Link vận đơn */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>--</StyledTableCell>

                      {/* Col 20: Đã giao hàng */}
                      <StyledTableCell align="center" style={{ cursor: 'pointer' }}>--</StyledTableCell>

                      {/* Col 21: Trạng thái */}
                      <StyledTableCell style={{ cursor: 'pointer' }}>
                        <Chip
                          label="Đã thu"
                          size="small"
                          style={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 700,
                            background: '#eafaf1',
                            color: '#27ae60',
                            border: '1px solid #27ae6033'
                          }}
                        />
                      </StyledTableCell>

                      {/* Thao tác */}
                      <StyledTableCell align="center" style={{ cursor: 'pointer' }} onClick={e => e.stopPropagation()}>
                        {canManageReceipts ? (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReceiptMenuOpen(e, receipt);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <MoreVertIcon fontSize="small" style={{ color: '#ccc' }} />
                        )}
                      </StyledTableCell>
                    </TableRow>
                  );
                }

                const order = item;
                const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG[3] || STATUS_CONFIG[0];
                const isPartSale = order.buy_method === 'Bán phụ kiện' || order.buyMethod === 'Bán phụ kiện' || order.order_type === 'PART_SALE' || order.orderType === 'PART_SALE';
                const orderGrandTotal = Number(order.total_amount || order.totalAmount || order.amount || 0);

                // Receipts linked to this order if any:
                const oIdKey = String(order.id || '');
                const oCodeKey = String(order.code || order.order_code || order.orderCode || '').trim().toLowerCase();
                const orderReceipts = [
                  ...(receiptsByOrderId && receiptsByOrderId[oIdKey] ? receiptsByOrderId[oIdKey] : []),
                  ...(oCodeKey && receiptsByOrderId && receiptsByOrderId[oCodeKey] ? receiptsByOrderId[oCodeKey] : [])
                ].filter((r, idx, arr) => arr.findIndex(x => (x.id && x.id === r.id) || (x.code && x.code === r.code)) === idx);
                const receiptSum = orderReceipts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

                // Raw paid from order/transaction object:
                let rawPaid = 0;
                const rawPaidVal = order.paid !== undefined ? order.paid : order.paid_amount;
                if (typeof rawPaidVal === 'number') {
                  if (rawPaidVal === 1 && orderGrandTotal > 100) {
                    rawPaid = orderGrandTotal;
                  } else {
                    rawPaid = rawPaidVal;
                  }
                } else if (rawPaidVal === true) {
                  rawPaid = orderGrandTotal;
                }

                const calculatedPaid = Math.max(rawPaid, receiptSum);
                const calculatedDebt = Math.max(0, orderGrandTotal - calculatedPaid);
                const calculatedOver = Math.max(0, calculatedPaid - orderGrandTotal);

                let calculatedStatus = 'UNPAID';
                if (order.status === -1) {
                  calculatedStatus = 'CANCELLED';
                } else if (orderGrandTotal > 0 && calculatedPaid > orderGrandTotal) {
                  calculatedStatus = 'OVERPAID';
                } else if (orderGrandTotal > 0 && calculatedPaid === orderGrandTotal) {
                  calculatedStatus = 'PAID';
                } else if (calculatedPaid > 0) {
                  calculatedStatus = 'PARTIAL';
                }

                const isAgencyCust = isAgencyCustomerOrder(order);
                const agId = String(order.agency_id || order.agency?.id || '');
                const agNameKey = (order.agency?.name || order.customer_name || '').trim().toLowerCase();
                const agencyInfo = (isAgencyCust && agencyDebtMap) ? (agencyDebtMap[agId] || agencyDebtMap[agNameKey] || null) : null;

                const defaultPaymentInfo = {
                  status: calculatedStatus,
                  paidAmount: calculatedPaid,
                  debtAmount: calculatedDebt,
                  overAmount: calculatedOver,
                  receipts: orderReceipts,
                  isAgency: Boolean(isAgencyCust),
                  agencyId: agId || agencyInfo?.id,
                  agencyName: order.agency?.name || agencyInfo?.name || order.customer_name || '',
                  agencyDebt: agencyInfo?.debt,
                  agencyTotalSales: agencyInfo?.totalSales,
                  agencyTotalPaid: agencyInfo?.totalPaid,
                  agencyInfo: agencyInfo
                };

                const paymentInfo = getOrderPaymentInfo ? getOrderPaymentInfo(order) : defaultPaymentInfo;
                const { status, paidAmount, debtAmount, receipts } = paymentInfo;
                const overAmount = paymentInfo.overAmount !== undefined ? paymentInfo.overAmount : Math.max(0, (paidAmount || 0) - orderGrandTotal);

                const handleOpenReceiptCreate = (e) => {
                  if (e) e.stopPropagation();
                  if (onOpenAddReceipt) {
                    const isAgencyCust = Boolean(paymentInfo?.isAgency);
                    const orderCode = order.code || order.order_code || order.orderCode || order.id || '';
                    if (isAgencyCust) {
                      const agId = order.agency_id || order.agency?.id || paymentInfo?.agencyId;
                      const agName = order.agency?.name || paymentInfo?.agencyName || order.customer_name || '';
                      const pPhone = order.agency?.leader?.phone || order.agency?.phone || '';
                      const pAddr = order.agency?.leader?.address || order.agency?.address || '';
                      const debt = paymentInfo?.agencyDebt !== undefined ? paymentInfo.agencyDebt : 0;

                      onOpenAddReceipt({
                        payer_type: 'AGENCY',
                        payer_id: agId,
                        payer_name: agName,
                        customer_name: agName,
                        phone: pPhone,
                        customer_phone: pPhone,
                        address: pAddr,
                        company: pAddr,
                        amount: debt > 0 ? debt : 0,
                        agency_debt: debt,
                        note: `Thu tiền công nợ Đại lý ${agName}`
                      });
                    } else {
                      const pId = order.end_user_id || order.end_user?.id || order.customer_id;
                      const pName = order.end_user?.full_name || order.customer_name || order.customerName || '';
                      const pPhone = order.customer_phone || order.phone || order.phone_number || '';
                      const pAddr = order.address || order.customer_address || order.shipping_address || '';
                      const orderCode = order.code || order.order_code || order.orderCode || order.id || '';

                      onOpenAddReceipt({
                        order_id: order.id,
                        order_code: orderCode,
                        payer_type: 'END_USER',
                        payer_id: pId,
                        payer_name: pName,
                        customer_name: pName,
                        customer_phone: pPhone,
                        phone: pPhone,
                        address: pAddr,
                        company: pAddr,
                        amount: debtAmount > 0 ? debtAmount : orderGrandTotal,
                        order_debt: debtAmount > 0 ? debtAmount : orderGrandTotal,
                        order_total: orderGrandTotal,
                        note: orderCode ? `Thu tiền đơn hàng #${orderCode}` : ''
                      });
                    }
                  }
                };

                return (
                  <TableRow
                    key={order.id}
                      hover
                      className={classes.tableRow}
                      style={{
                        cursor: 'pointer'
                      }}
                      onClick={e => handleRowClick(e, order)}
                    >
                    <StyledTableCell
                      className={classes.stickyCol}
                      style={{ left: 0, width: 80, minWidth: 80 }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography
                          variant="body2"
                          style={{
                            fontSize: 13,
                            color: cfg.color || '#666',
                            fontWeight: 600
                          }}
                        >
                          {order.created_at || order.createdAt
                            ? (() => {
                              const d = new Date(
                                order.created_at || order.createdAt
                              );
                              return !isNaN(d.getTime())
                                ? `${String(d.getDate()).padStart(
                                  2,
                                  '0'
                                )}/${String(d.getMonth() + 1).padStart(
                                  2,
                                  '0'
                                )}/${d.getFullYear()}`
                                : 'N/A';
                            })()
                            : 'N/A'}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ fontSize: 13, color: cfg.color || '#888' }}
                        >
                          {order.created_at || order.createdAt
                            ? (() => {
                              const d = new Date(
                                order.created_at || order.createdAt
                              );
                              return !isNaN(d.getTime())
                                ? `${String(d.getHours()).padStart(
                                  2,
                                  '0'
                                )}:${String(d.getMinutes()).padStart(
                                  2,
                                  '0'
                                )}`
                                : '';
                            })()
                            : ''}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell
                      className={classes.stickyCol}
                      style={{
                        left: 80,
                        color: '#1a1f36',
                        fontSize: 13,
                        fontWeight: 600,
                        width: 110,
                        minWidth: 110
                      }}
                    >
                      <Typography style={{ fontSize: 13, fontWeight: 700, color: '#1a1f36' }}>
                        {order.code || '--'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      className={classes.stickyCol}
                      style={{ left: 190, width: 120, minWidth: 120 }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography
                          variant="body2"
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#3f51b5'
                          }}
                        >
                          {order.sale?.full_name ||
                            order.sale?.name ||
                            order.user?.username ||
                            order.user?.full_name ||
                            order.creator?.full_name ||
                            order.creator?.name ||
                            (
                              order.sale_name ||
                              order.saleName ||
                              order.creator_name ||
                              order.created_by_name ||
                              'Hệ thống'
                            )
                              .split('(')[0]
                              .trim()}
                        </Typography>
                        {order.created_by && (order.creator?.full_name || order.created_by_name) && order.created_by !== (order.sale_id || order.sale?.id) && (
                          <Typography
                            variant="caption"
                            style={{
                              fontSize: 11,
                              color: '#d97706',
                              fontWeight: 500,
                              lineHeight: 1.2,
                              marginTop: 2
                            }}
                          >
                            Tạo bởi: {order.creator?.full_name || order.created_by_name}
                          </Typography>
                        )}
                        {(() => {
                          const saleId = order.sale_id || order.sale?.id || order.saleId;
                          const creatorId = order.created_by || order.creator?.id;
                          const rawSaleName = (
                            order.sale?.full_name ||
                            order.sale?.name ||
                            order.user?.username ||
                            order.user?.full_name ||
                            order.sale_name ||
                            order.saleName ||
                            ''
                          ).trim();
                          const rawCreatorName = (order.creator?.full_name || order.creator?.name || order.created_by_name || '').trim();
                          const staffName = (rawSaleName || rawCreatorName || 'Hệ thống').split('(')[0].trim();

                          if (staffName === 'Hệ thống' || !staffName) {
                            return (
                              <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>
                                Hệ thống
                              </Typography>
                            );
                          }

                          // 1. Check sale.role_id (just like original code)
                          const roleId = Number(order.sale?.role_id || order.sale?.roleId || 0);
                          if (roleId === 1) return <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>Quản trị</Typography>;
                          if (roleId === 2) return <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>Sale</Typography>;
                          if (roleId === 3 || roleId === 7) return <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>Kế toán</Typography>;
                          if (roleId === 8) return <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>Kỹ thuật</Typography>;
                          if (roleId === 11) return <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>Lead Marketing</Typography>;

                          // 2. Check user.role
                          if (order.user?.role) {
                            const uRole = String(order.user.role).toUpperCase();
                            if (uRole === 'ADMIN') return <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>Quản trị</Typography>;
                            if (uRole === 'SALE') return <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>Sale</Typography>;
                            if (uRole === 'ACCOUNTANT') return <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>Kế toán</Typography>;
                          }

                          // 3. Parentheses role
                          if (rawSaleName.includes('(')) {
                            const ext = rawSaleName.split('(')[1].replace(')', '').trim();
                            if (ext) return <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>{ext}</Typography>;
                          }

                          // 4. Lookup from usersLookupMap (staff, sales, dataLogin)
                          const matchedOrderUser =
                            (saleId && usersLookupMap.byId[String(saleId)]) ||
                            (creatorId && usersLookupMap.byId[String(creatorId)]) ||
                            (staffName && usersLookupMap.byName[staffName.toLowerCase()]) ||
                            (staffName && usersLookupMap.byUsername[staffName.toLowerCase()]) ||
                            (dataLogin && (dataLogin.id === saleId || dataLogin.id === creatorId || (dataLogin.full_name && dataLogin.full_name.trim().toLowerCase() === staffName.toLowerCase())) ? dataLogin : null);

                          const resolvedRole = getRoleDisplayName(matchedOrderUser, '');
                          if (resolvedRole && resolvedRole !== 'Hệ thống') {
                            return (
                              <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>
                                {resolvedRole}
                              </Typography>
                            );
                          }

                          // 5. Default fallback for real staff on sales order is 'Sale'
                          return (
                            <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, color: '#888' }}>
                              Sale
                            </Typography>
                          );
                        })()}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell
                      className={classes.stickyCol}
                      style={{
                        left: 310,
                        width: 190,
                        minWidth: 190,
                        borderRight: isScrolled
                          ? '1px solid rgba(0,0,0,0.8)'
                          : 'none',
                        boxShadow: isScrolled
                          ? '6px 0 10px rgba(0,0,0,0.15)'
                          : 'none',
                        transition: 'border-right 0.2s, box-shadow 0.2s'
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Tooltip
                          title={
                            order.end_user?.full_name ||
                            order.agency?.name ||
                            order.customer_name ||
                            '--'
                          }
                          arrow
                          placement="top-start"
                        >
                          <Typography
                            className={classes.customerName}
                            style={{
                              color:
                                isAgencyCustomerOrder(order)
                                  ? '#3f51b5'
                                  : '#1a1f36',
                              whiteSpace: 'normal'
                            }}
                          >
                            {order.end_user?.full_name ||
                              (isAgencyCustomerOrder(order) ? order.agency?.name : '') ||
                              order.customer_name ||
                              order.customerName ||
                              order.agency?.name ||
                              '--'}
                          </Typography>
                        </Tooltip>
                        {(() => {
                          const isAgOrder = isAgencyCustomerOrder(order);
                          const agId = String(order.agency_id || order.agency?.id || '');
                          const pNameKey = (
                            order.end_user?.full_name ||
                            (isAgOrder ? order.agency?.name : '') ||
                            order.customer_name ||
                            (typeof order.counterparty === 'string' ? order.counterparty : order.counterparty?.name) ||
                            ''
                          ).trim().toLowerCase();
                          const phoneNum =
                            order.customer_phone ||
                            order.phone ||
                            order.phone_number ||
                            order.end_user?.phone ||
                            (isAgOrder ? (order.agency?.leader?.phone || order.agency?.phone || (agId && agencyPhoneMap[agId]) || (pNameKey && agencyPhoneMap[pNameKey])) : '') ||
                            '';
                          return phoneNum ? (
                            <Tooltip title="Click để sao chép SĐT" arrow placement="bottom-start">
                              <Box
                                display="inline-flex"
                                alignItems="center"
                                style={{ cursor: 'pointer', gap: 4, width: 'fit-content', marginTop: 2 }}
                                onClick={e => handleCopyPhone(e, phoneNum)}
                              >
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontSize: 13,
                                    color: '#666',
                                    fontWeight: 500,
                                    transition: 'color 0.2s'
                                  }}
                                  onMouseEnter={e => (e.target.style.color = '#3f51b5')}
                                  onMouseLeave={e => (e.target.style.color = '#666')}
                                >
                                  {phoneNum}
                                </Typography>
                                <FileCopyOutlinedIcon
                                  style={{
                                    fontSize: 13,
                                    color: '#888',
                                    transition: 'color 0.2s'
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="body2"
                              style={{ fontSize: 13, color: '#888' }}
                            >
                              --
                            </Typography>
                          );
                        })()}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell style={{ minWidth: 220 }}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ fontSize: 13, whiteSpace: 'normal' }}
                      >
                        {(() => {
                          const isAgOrder = isAgencyCustomerOrder(order);
                          const agId = String(order.agency_id || order.agency?.id || '');
                          const pNameKey = (
                            order.end_user?.full_name ||
                            (isAgOrder ? order.agency?.name : '') ||
                            order.customer_name ||
                            (typeof order.counterparty === 'string' ? order.counterparty : order.counterparty?.name) ||
                            ''
                          ).trim().toLowerCase();
                          return (
                            order.address ||
                            order.customer_address ||
                            order.shipping_address ||
                            order.end_user?.address ||
                            (isAgOrder ? (order.agency?.leader?.address || order.agency?.address || (agId && agencyAddressMap[agId]) || (pNameKey && agencyAddressMap[pNameKey])) : '') ||
                            '--'
                          );
                        })()}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell style={{ minWidth: 320 }}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ fontSize: 13, whiteSpace: 'normal' }}
                      >
                        {order.description || '--'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box
                        display="flex"
                        flexDirection="column"
                        style={{ gap: 4, maxWidth: 220 }}
                      >
                        <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                          {(
                            order.order_device_types ||
                            order.devices ||
                            []
                          ).map((d, i) => {
                            const typeName =
                              d.device_type?.name ||
                              d.device_type_name ||
                              d.type ||
                              listDeviceType.find(
                                lt => lt.id === d.device_type_id
                              )?.name ||
                              'N/A';
                            return (
                              <Chip
                                key={i}
                                size="small"
                                label={`${d.quantity} x ${typeName}`}
                                style={{
                                  background: '#e8f0fe',
                                  color: '#1a73e8',
                                  fontWeight: 600,
                                  fontSize: 11,
                                  height: 20
                                }}
                              />
                            );
                          })}
                        </Box>
                        <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                          {(
                            order.order_products ||
                            order.products ||
                            []
                          ).map((p, i) => {
                            const pCode = p.product_code || p.product?.code || '';
                            const pName = p.product_name || p.product?.name || '';
                            return (
                              <Chip
                                key={`p-${i}`}
                                label={`${p.quantity} x ${pCode || pName}`}
                                size="small"
                                style={{
                                  background: '#e8f5e9',
                                  color: '#2e7d32',
                                  fontWeight: 600,
                                  fontSize: 11,
                                  height: 20
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      {isPartSale ? (
                        '--'
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={order.status === -1}
                          onClick={e => {
                            e.stopPropagation();
                            if (onOpenSerialModal) {
                              onOpenSerialModal(order);
                              return;
                            }
                            setEditingOrder(order);
                            const initialDevices = (
                              order.order_device_types ||
                              order.devices ||
                              []
                            ).map(odt => ({
                              ...JSON.parse(JSON.stringify(odt)),
                              device_type_name:
                                odt.device_type?.name ||
                                odt.device_type_name ||
                                odt.type ||
                                listDeviceType.find(
                                  lt => lt.id === odt.device_type_id
                                )?.name ||
                                'N/A',
                              device_ids:
                                odt.order_details?.map(od => od.device_id).filter(Boolean) ||
                                odt.device_ids ||
                                [],
                              serials:
                                odt.order_details?.map(
                                  od =>
                                    od.serial ||
                                    od.device?.serial ||
                                    od.device?.imei
                                ).filter(Boolean) ||
                                odt.serials ||
                                []
                            }));
                            setEditingDevices(initialDevices);
                          }}
                          style={{
                            height: 24,
                            fontSize: 11,
                            textTransform: 'none',
                            background:
                              order.status >= 3 ||
                              (
                                order.order_device_types || order.devices
                              )?.every(
                                d =>
                                  (d.order_details || d.serials)?.length >=
                                  d.quantity
                              )
                                ? 'transparent'
                                : '#eaf4fb',
                            borderColor:
                              order.status >= 3 ||
                              (
                                order.order_device_types || order.devices
                              )?.every(
                                d =>
                                  (d.order_details || d.serials)?.length >=
                                  d.quantity
                              )
                                ? '#3f51b560'
                                : '#2980b9',
                            color:
                              order.status >= 3 ||
                              (
                                order.order_device_types || order.devices
                              )?.every(
                                d =>
                                  (d.order_details || d.serials)?.length >=
                                  d.quantity
                              )
                                ? '#3f51b5'
                                : '#2980b9'
                          }}
                        >
                          {isAgency ||
                          order.status >= 3 ||
                          (order.order_device_types || order.devices)?.every(
                            d =>
                              (d.order_details || d.serials)?.length >=
                              d.quantity
                          )
                            ? 'Xem Serial'
                            : '+ Thêm Serial'}
                        </Button>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {isPartSale ? (
                        '--'
                      ) : (
                        <Box display="flex" flexDirection="column" style={{ gap: 2 }}>
                          {(order.sim_method || order.simMethod || '--')
                            .replace(/Vgps/g, 'VGPS')
                            .split(';')
                            .map(s => s.trim())
                            .filter(Boolean)
                            .map((simStr, sIdx) => (
                              <Typography
                                key={sIdx}
                                variant="body2"
                                style={{
                                  fontSize: 12,
                                  color: '#333',
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {simStr}
                              </Typography>
                            ))}
                        </Box>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {isPartSale ? (
                        '--'
                      ) : (
                        <Typography
                          variant="body2"
                          style={{ fontSize: 13, color: '#333' }}
                        >
                          {(order.active_method || order.activeMethod || '--').replace(/Vgps/g, 'VGPS')}
                        </Typography>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {isPartSale ? (
                        '--'
                      ) : (
                        <Typography
                          variant="body2"
                          style={{ fontSize: 13, color: '#333' }}
                        >
                          {(order.buy_method || order.buyMethod || '--').replace(/Vgps/g, 'VGPS')}
                        </Typography>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {isPartSale ? (
                        '--'
                      ) : (
                        <Typography
                          variant="body2"
                          style={{ fontSize: 13, color: '#333' }}
                        >
                          {(order.installer || '--').replace(/Vgps/g, 'VGPS')}
                        </Typography>
                      )}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {isPartSale ? (
                        '--'
                      ) : !(isSale || isLeadMarketing || isAgency || order.status >= 3 || order.status === -1) ? (
                        <Tooltip title="Tải ảnh lắp đặt">
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              setOrderForImage(order);
                              setOpenImageModal(true);
                            }}
                            style={{
                              color: '#1a73e8',
                              backgroundColor: '#e8f0fe',
                              border: '1px solid #1a73e844',
                              borderRadius: '4px',
                              padding: '4px'
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <EditIcon fontSize="small" style={{ color: '#ccc' }} />
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {isPartSale ? (
                        '--'
                      ) : (
                        <Box
                          display="flex"
                          style={{ gap: 4, alignItems: 'center' }}
                        >
                          {(order.order_images || [])
                            .slice(0, 2)
                            .map((img, i) => (
                              <Box
                                key={i}
                                onClick={e => {
                                  e.stopPropagation();
                                  setSelectedGallery({
                                    images: order.order_images,
                                    index: i,
                                    orderId: order.id,
                                    order_status: order.status
                                  });
                                }}
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 4,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  border: '1px solid #eee'
                                }}
                              >
                                <img
                                  src={`${process.env.REACT_APP_IMG_SERVER}/${img.url}`}
                                  alt="order"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                              </Box>
                            ))}
                          {(order.order_images || []).length > 2 && (
                            <Typography
                              variant="caption"
                              style={{
                                color: '#3f51b5',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            onClick={e => {
                              e.stopPropagation();
                              setSelectedGallery({
                                images: order.order_images,
                                index: 2,
                                orderId: order.id,
                                order_status: order.status
                              });
                            }}
                            >
                              +{(order.order_images || []).length - 2}
                            </Typography>
                          )}
                          {(order.order_images || []).length === 0 && '--'}
                        </Box>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box>
                        <Typography
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#1a1f36'
                          }}
                        >
                          {(() => {
                            const deviceList = order.order_device_types || order.devices || [];
                            const productList = order.order_products || order.products || [];
                            const dSub = deviceList.reduce(
                              (sum, d) => sum + (d.quantity || 0) * (d.unit_price || d.unitPrice || 0),
                              0
                            );
                            const pSub = productList.reduce(
                              (sum, p) => sum + (p.quantity || 0) * (p.unit_price || p.unitPrice || 0) - (p.discount || 0),
                              0
                            );
                            return formatMoney(dSub + pSub);
                          })()}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <TextField
                        size="small"
                        placeholder="0"
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        disabled={
                          isAgency ||
                          !(isAdmin || isAccountant) ||
                          isLeadMarketing ||
                          order.status === 3 ||
                          order.status === -1
                        }
                        value={
                          localExtraFees[order.id] !== undefined
                            ? localExtraFees[order.id]
                            : formatInputMoney(order.extra_fee || 0)
                        }
                        onChange={e =>
                          setLocalExtraFees({
                            ...localExtraFees,
                            [order.id]: e.target.value
                          })
                        }
                        onFocus={e => {
                          if (localExtraFees[order.id] === undefined) {
                            setLocalExtraFees({
                              ...localExtraFees,
                              [order.id]: formatInputMoney(
                                order.extra_fee || 0
                              )
                            });
                          }
                        }}
                        onBlur={() => handleSaveExtraFee(order)}
                        variant="outlined"
                        InputProps={{
                          style: { fontSize: 12, height: 32, width: 130 },
                          endAdornment: (
                            <Typography
                              style={{
                                fontSize: 10,
                                color: '#888',
                                whiteSpace: 'nowrap',
                                marginLeft: 2
                              }}
                            >
                              đ
                            </Typography>
                          )
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box>
                        <Typography
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#e67e22'
                          }}
                        >
                          {formatMoney(
                            order.total_amount || order.totalAmount || 0
                          )}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      {(() => {
                        const method =
                          order.payment_method || order.paymentMethod;
                        const style = getPaymentStyle(method);
                        return (
                          <Typography
                            variant="body2"
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: style.color,
                              background: style.bg,
                              padding: '4px 10px',
                              borderRadius: 12,
                              display: 'inline-block',
                              border: `1px solid ${style.color}22`
                            }}
                          >
                            {method || '--'}
                          </Typography>
                        );
                      })()}
                    </StyledTableCell>
                    <StyledTableCell onClick={e => e.stopPropagation()}>
                      {order.status === -1 ? (
                        <Chip
                          label="Đã hủy"
                          size="small"
                          style={{ height: 22, fontSize: 11, background: '#f5f5f5', color: '#999' }}
                        />
                      ) : paymentInfo.isAgency ? (
                        /* ĐẠI LÝ: Theo dõi theo Công nợ tổng gom nhiều đơn */
                        paymentInfo.agencyDebt <= 0 && (paymentInfo.agencyTotalSales || 0) > 0 ? (
                          <Tooltip title={`Đại lý đã thanh toán đủ: ${formatMoney(paymentInfo.agencyTotalPaid || 0)} / Tổng mua: ${formatMoney(paymentInfo.agencyTotalSales || 0)}`}>
                            <Chip
                              label="ĐL hết nợ"
                              size="small"
                              style={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 700,
                                background: '#eafaf1',
                                color: '#27ae60',
                                border: '1px solid #27ae6044',
                                cursor: 'pointer'
                              }}
                              onClick={handleOpenReceiptCreate}
                            />
                          </Tooltip>
                        ) : (paymentInfo.agencyDebt || 0) > 0 ? (
                          <Tooltip title={`Tổng mua: ${formatMoney(paymentInfo.agencyTotalSales || 0)} • Đã thu: ${formatMoney(paymentInfo.agencyTotalPaid || 0)} • Click để lập phiếu thu`}>
                            <Chip
                              label={`Nợ ĐL: ${formatMoney(paymentInfo.agencyDebt)}`}
                              size="small"
                              style={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 700,
                                background: '#fff1f0',
                                color: '#cf1322',
                                border: '1px solid #ffa39e',
                                cursor: 'pointer'
                              }}
                              onClick={handleOpenReceiptCreate}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title={`Tổng mua: ${formatMoney(paymentInfo.agencyTotalSales || 0)} • Đã thu: ${formatMoney(paymentInfo.agencyTotalPaid || 0)}`}>
                            <Chip
                              label="Chưa phát sinh nợ"
                              size="small"
                              style={{
                                height: 22,
                                fontSize: 11,
                                background: '#f5f5f5',
                                color: '#777',
                                cursor: 'pointer'
                              }}
                              onClick={handleOpenReceiptCreate}
                            />
                          </Tooltip>
                        )
                      ) : (
                        /* KHÁCH LẺ: Thu tiền theo từng đơn */
                        status === 'OVERPAID' || (orderGrandTotal > 0 && paidAmount > orderGrandTotal) ? (
                          <Tooltip title={`Đã thu: ${formatMoney(paidAmount)} / Tổng đơn: ${formatMoney(orderGrandTotal)} • Thu thừa: +${formatMoney(overAmount || (paidAmount - orderGrandTotal))}`}>
                            <Chip
                              label={`Thu thừa: +${formatMoney(overAmount || (paidAmount - orderGrandTotal))}`}
                              size="small"
                              style={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 700,
                                background: '#f3e8ff',
                                color: '#7e22ce',
                                border: '1px solid #d8b4fe'
                              }}
                            />
                          </Tooltip>
                        ) : status === 'PAID' ? (
                          <Tooltip title={`Đã thu đủ: ${formatMoney(paidAmount)}`}>
                            <Chip
                              label="Đã thu đủ"
                              size="small"
                              style={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 700,
                                background: '#eafaf1',
                                color: '#27ae60',
                                border: '1px solid #27ae6044'
                              }}
                            />
                          </Tooltip>
                        ) : status === 'PARTIAL' ? (
                          <Tooltip title={`Đã thu: ${formatMoney(paidAmount)} / Tổng đơn: ${formatMoney(orderGrandTotal)}`}>
                            <Chip
                              label={`Thiếu: ${formatMoney(debtAmount)}`}
                              size="small"
                              style={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 700,
                                background: '#fef9e7',
                                color: '#d35400',
                                border: '1px solid #f39c1244'
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title={`Tổng đơn: ${formatMoney(orderGrandTotal)}`}>
                            <Chip
                              label="Chưa thanh toán"
                              size="small"
                              style={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 600,
                                background: '#fff1f0',
                                color: '#cf1322',
                                border: '1px solid #ffa39e'
                              }}
                            />
                          </Tooltip>
                        )
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Tooltip
                        title={
                          (!isAdmin && !isPartSale && order.status < 1)
                            ? 'Cần nhập Serial thiết bị trước'
                            : ''
                        }
                      >
                        <span>
                          <TextField
                            size="small"
                            placeholder="Dán link vận đơn..."
                            onClick={e => e.stopPropagation()}
                            onMouseDown={e => e.stopPropagation()}
                            disabled={
                              isAgency ||
                              (!isAdmin && !isPartSale && order.status < 1) ||
                              order.status >= 3 ||
                              isSale ||
                              isLeadMarketing ||
                              order.delivery_method === 0 ||
                              order.installer?.replace(/Vgps/g, 'VGPS') === 'VGPS lắp đặt'
                            }
                            value={
                              localTrackingUrls[order.id] !== undefined
                                ? localTrackingUrls[order.id]
                                : order.delivery_method === 0 ||
                                  order.installer?.replace(/Vgps/g, 'VGPS') === 'VGPS lắp đặt'
                                  ? 'offline'
                                  : order.tracking_number || ''
                            }
                            onChange={e =>
                              setLocalTrackingUrls({
                                ...localTrackingUrls,
                                [order.id]: e.target.value
                              })
                            }
                            onBlur={() => handleSaveTrackingUrl(order)}
                            variant="outlined"
                            InputProps={{
                              style: {
                                fontSize: 11,
                                height: 32,
                                width: 180
                              },
                              endAdornment:
                                order.tracking_number ||
                                  localTrackingUrls[order.id] ? (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={e => {
                                        e.stopPropagation();
                                        let url = order.tracking_number || localTrackingUrls[order.id];
                                        if (url && !/^https?:\/\//i.test(url)) {
                                          url = 'http://' + url;
                                        }
                                        window.open(url, '_blank');
                                      }}
                                    >
                                      <OpenInNewIcon
                                        style={{ fontSize: 16 }}
                                      />
                                    </IconButton>
                                  </InputAdornment>
                                ) : null
                            }}
                          />
                        </span>
                      </Tooltip>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {!((!isPartSale && order.status < 2) || order.status === -1 || isSale || isLeadMarketing || isAgency) ? (
                        <Tooltip
                          title={
                            order.status >= 3
                              ? 'Hủy xác nhận đã giao hàng'
                              : 'Xác nhận đã giao hàng'
                          }
                        >
                          <span>
                            {order.status >= 3 ? (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeliveredToggle(order, false);
                                }}
                                style={{
                                  textTransform: 'none',
                                  fontSize: '11px',
                                  height: '24px',
                                  backgroundColor: '#27ae60',
                                  color: '#fff',
                                  boxShadow: 'none'
                                }}
                              >
                                Đã giao
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeliveredToggle(order, true);
                                }}
                                style={{
                                  textTransform: 'none',
                                  fontSize: '11px',
                                  height: '24px',
                                  color: '#27ae60',
                                  borderColor: '#27ae6088',
                                  backgroundColor: '#eafaf1'
                                }}
                              >
                                Giao hàng
                              </Button>
                            )}
                          </span>
                        </Tooltip>
                      ) : (
                        <span>
                          {order.status >= 3 ? (
                            <CheckCircleIcon
                              style={{
                                color: '#27ae60'
                              }}
                              fontSize="small"
                            />
                          ) : (
                            '--'
                          )}
                        </span>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={cfg.label}
                        size="small"
                        style={{
                          background: cfg.bg,
                          color: cfg.color,
                          fontWeight: 700,
                          fontSize: 11,
                          border: `1px solid ${cfg.color}33`
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          handleMenuOpen(e, order);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={23} align="center" style={{ padding: 40 }}>
                  <Typography variant="body1" color="textSecondary">
                    Không tìm thấy dữ liệu đơn hàng...
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {paginated && paginated.length > 0 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          style={{
            width: '100%',
            padding: '8px 14px',
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            gap: '8px 16px'
          }}
        >
          {/* Cụm trái: Thống kê các khoản tiền siêu gọn đẹp */}
          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            style={{ gap: '6px 8px', flex: '1 1 auto' }}
          >
            {(() => {
              const isReceiptMode = transactionTypeFilter === 'RECEIPTS';

              if (isReceiptMode) {
                const totalReceiptAmount = receiptTotalAmountSum || totalAmountSum || 0;
                return (
                  <Box
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      borderRadius: 6,
                      backgroundColor: '#ecfdf5',
                      border: '1px solid #a7f3d0'
                    }}
                  >
                    <Typography style={{ fontSize: 11.5, color: '#047857', fontWeight: 600 }}>
                      Tổng tiền phiếu thu:
                    </Typography>
                    <strong style={{ fontSize: 12, color: '#065f46', fontWeight: 700 }}>
                      {formatMoney(totalReceiptAmount)}
                    </strong>
                  </Box>
                );
              }

              const orderTotal = (isUnifiedMode && unifiedSummary?.order_amount_sum !== undefined)
                ? Number(unifiedSummary.order_amount_sum)
                : (Number(totalAmountSum) || 0);

              const paidTotal = (isUnifiedMode && (unifiedSummary?.receipt_amount_sum !== undefined || unifiedSummary?.total_cash_in !== undefined))
                ? Number(unifiedSummary.receipt_amount_sum !== undefined ? unifiedSummary.receipt_amount_sum : unifiedSummary.total_cash_in)
                : (Number(totalPaidSum) || 0);

              const debtTotal = Math.max(0, orderTotal - paidTotal);

              return (
                <>
                  {/* 1. Tổng tiền */}
                  <Box
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      borderRadius: 6,
                      backgroundColor: '#f5f6ff',
                      border: '1px solid #c7d2fe'
                    }}
                  >
                    <Typography style={{ fontSize: 11.5, color: '#4338ca', fontWeight: 600 }}>
                      {isUnifiedMode ? 'Tổng tiền đơn:' : 'Tổng tiền:'}
                    </Typography>
                    <strong style={{ fontSize: 12, color: '#312e81', fontWeight: 700 }}>
                      {formatMoney(orderTotal)}
                    </strong>
                  </Box>

                  {/* 2. Thực thu & Còn nợ (Khi ở chế độ dòng tiền hợp nhất) */}
                  {isUnifiedMode && (
                    <>
                      <Box
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '3px 8px',
                          borderRadius: 6,
                          backgroundColor: '#ecfdf5',
                          border: '1px solid #a7f3d0'
                        }}
                      >
                        <Typography style={{ fontSize: 11.5, color: '#047857', fontWeight: 600 }}>
                          Đã nhận:
                        </Typography>
                        <strong style={{ fontSize: 12, color: '#065f46', fontWeight: 700 }}>
                          {formatMoney(paidTotal)}
                        </strong>
                      </Box>

                      <Box
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '3px 8px',
                          borderRadius: 6,
                          backgroundColor: '#fffbeb',
                          border: '1px solid #fde68a'
                        }}
                      >
                        <Typography style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600 }}>
                          nợ:
                        </Typography>
                        <strong style={{ fontSize: 12, color: '#92400e', fontWeight: 700 }}>
                          {formatMoney(debtTotal)}
                        </strong>
                      </Box>
                    </>
                  )}
                </>
              );
            })()}

            {/* 3. Chi tiết: Tiền thiết bị, phụ kiện, ship & lắp đặt, VAT, giảm giá */}
            {transactionTypeFilter !== 'RECEIPTS' && amountBreakdown && (
              <>
                <Box
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 8px',
                    borderRadius: 6,
                    backgroundColor: '#f8fafc',
                    border: '1px solid #cbd5e1'
                  }}
                >
                  <Typography style={{ fontSize: 11.5, color: '#475569', fontWeight: 500 }}>
                    Thiết bị:
                  </Typography>
                  <strong style={{ fontSize: 12, color: '#0f172a', fontWeight: 600 }}>
                    {formatMoney(amountBreakdown.device_amount_sum || 0)}
                  </strong>
                </Box>

                {(amountBreakdown.product_amount_sum || 0) > 0 && (
                  <Box
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      borderRadius: 6,
                      backgroundColor: '#f0fdf4',
                      border: '1px solid #bbf7d0'
                    }}
                  >
                    <Typography style={{ fontSize: 11.5, color: '#15803d', fontWeight: 500 }}>
                      Phụ kiện:
                    </Typography>
                    <strong style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>
                      {formatMoney(amountBreakdown.product_amount_sum)}
                    </strong>
                  </Box>
                )}

                {(amountBreakdown.extra_fee_sum || 0) > 0 && (
                  <Box
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      borderRadius: 6,
                      backgroundColor: '#fff7ed',
                      border: '1px solid #fed7aa'
                    }}
                  >
                    <Typography style={{ fontSize: 11.5, color: '#c2410c', fontWeight: 500 }}>
                      Ship & Lắp:
                    </Typography>
                    <strong style={{ fontSize: 12, color: '#9a3412', fontWeight: 600 }}>
                      {formatMoney(amountBreakdown.extra_fee_sum)}
                    </strong>
                  </Box>
                )}

                {(amountBreakdown.vat_sum || 0) > 0 && (
                  <Box
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      borderRadius: 6,
                      backgroundColor: '#faf5ff',
                      border: '1px solid #e9d5ff'
                    }}
                  >
                    <Typography style={{ fontSize: 11.5, color: '#7e22ce', fontWeight: 500 }}>
                      VAT:
                    </Typography>
                    <strong style={{ fontSize: 12, color: '#6b21a8', fontWeight: 600 }}>
                      {formatMoney(amountBreakdown.vat_sum)}
                    </strong>
                  </Box>
                )}

                {(amountBreakdown.discount_sum || 0) > 0 && (
                  <Box
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      borderRadius: 6,
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca'
                    }}
                  >
                    <Typography style={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 500 }}>
                      Giảm giá:
                    </Typography>
                    <strong style={{ fontSize: 12, color: '#991b1b', fontWeight: 600 }}>
                      -{formatMoney(amountBreakdown.discount_sum)}
                    </strong>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Cụm phải: Phân trang gọn gàng */}
          <Box
            display="flex"
            alignItems="center"
            style={{
              gap: 12,
              marginLeft: 'auto'
            }}
          >
            <Typography style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
              Tổng: <strong style={{ color: '#0f172a' }}>{total || 0}</strong>
            </Typography>
            <Pagination
              count={Math.ceil((total || 0) / PAGE_SIZE)}
              color="primary"
              size="small"
              onChange={(_, p) => setPage(p)}
              page={page}
              siblingCount={1}
              boundaryCount={1}
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      )}


      {/* ── Menu Thao Tác Phiếu Thu ── */}
      <Menu
        anchorEl={receiptMenuAnchor}
        keepMounted
        open={Boolean(receiptMenuAnchor)}
        onClose={handleReceiptMenuClose}
        PaperProps={{
          style: {
            minWidth: 140,
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
          }
        }}
      >
        <MenuItem
          onClick={(e) => {
            if (e && e.stopPropagation) e.stopPropagation();
            if (menuReceipt && onEditReceipt) {
              onEditReceipt(menuReceipt);
            }
            handleReceiptMenuClose();
          }}
          style={{ fontSize: 13 }}
        >
          <ListItemIcon style={{ minWidth: 30, color: '#1976d2' }}>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Chỉnh sửa"
            primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 500 } }}
          />
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            if (e && e.stopPropagation) e.stopPropagation();
            if (menuReceipt && onDeleteReceipt) {
              onDeleteReceipt(menuReceipt);
            }
            handleReceiptMenuClose();
          }}
          style={{ fontSize: 13, color: '#d32f2f' }}
        >
          <ListItemIcon style={{ minWidth: 30, color: '#d32f2f' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Xóa phiếu"
            primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 500, color: '#d32f2f' } }}
          />
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default OrdersTable;
